import {
  Button,
  ICellColumnsProps,
  Icon,
  IMultiEntitySelectorCellProps,
  StatusCell,
  TColumns,
  TFColumn,
  Typography,
} from '@ts/storybook';
import Link from '@components/Outputs/Link';
import actStore from '@flux/actions/actStore';
import Tasks, { defaultConfigComponent as TConfigComponent } from '@pages/DataBase/Tasks/Tasks';
import { getGridSingleSelectOperators, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { generateGlobalColumns, generateDateColumn, generateMultiEntityColumn, generateEntityColumn } from '@ts/storybook';
import HierarchyDataGrid from '../ItemRelations/HierarchyDataGrid';
import GTItemTypesInLocation from '@modules/generalTab/GTItemTypeInLocation';
import { Tooltip } from '@components/Outputs';
import {
  CLocation,
  CDevice,
  formatDate,
  formatValue,
  CLabel,
  CStatusIndicator,
  FLAVORS,
  CElement,
  CElementType,
  CProcess,
} from '@ts/ui-models';
import { sortBy } from 'lodash';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import Notifications from '@pages/DataBase/Configuration/Notifications/Notifications';

export const onClickMultiEntityCell = (props: IMultiEntitySelectorCellProps, listIds: string[]) => {
  const { params } = props;
  const { optionsColumn, optionsValue } = props.options;

  const configComponent = generateSearchEntitySelector(optionsValue?.tEntity);

  actStore.setActionTableV3({
    title: optionsColumn?.headerName + (params.row.getName() !== 'Unknown' ? ' - ' + params.row.getName() : ''),
    action: 'openModalExtraComp',
    component: configComponent.component,
    propsComponent: {
      configComponent: {
        ...configComponent.configComponent,
        where: { logicalId: listIds },
      },
    },
    row: params.row,
  });
};

const ItemColumns = (props: ICellColumnsProps): Record<string, GridColDef<any>> => {
  const { inheritedProps } = props;
  return {
    id: generateGlobalColumns(props)['id'](),
    //customId: generateGlobalColumns(props)['customId'](),
    nameBasic: generateGlobalColumns(props)['nameBasic'](),
    name: generateGlobalColumns(props)['name'](undefined, props.configComponent.mainClass),
    flavors: {
      headerName: 'Flavors',
      field: 'flavors',
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);

        return tElement.getFlavors?.map(f => f).join(' | ') ?? '';
      },
      type: 'singleSelect',
      valueOptions: FLAVORS.map(f => ({ label: f.label, value: f.id })),
      filterOperators: getGridSingleSelectOperators().filter(operator => operator.value === 'is'),
    },
    status: {
      headerName: 'Status',
      field: 'status',
      filterable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);

        const listStatusIndicatorSortByName = sortBy(
          tElement.currentStatus?.indicators.map(i => new CStatusIndicator(i)),
          o => {
            return o.getName();
          }
        );
        return (
          listStatusIndicatorSortByName
            .map(st => {
              return st.getName() + ': ' + st.value;
            })
            .join(' | ') ?? ''
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const tElement = new CElement(params.row);

        return <StatusCell indicators={tElement.currentStatus?.indicators.map(i => new CStatusIndicator(i))} />;
      },
    },
    statusAlive: {
      headerName: 'Live status',
      field: 'statusAlive',
      filterable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);

        const listStatusIndicatorSortByNameAndFilter = sortBy(
          tElement.currentStatus?.indicators.map(i => new CStatusIndicator(i)).filter(i => i.readingType && i.update.live),
          o => {
            return o.getName();
          }
        );
        return (
          listStatusIndicatorSortByNameAndFilter
            .map(i => {
              return i.getName() + ': ' + i.value;
            })
            .join(' | ') ?? ''
        );
      },
      renderCell: (params: GridRenderCellParams) => {
        const tElement = new CElement(params.row);

        return (
          <StatusCell
            indicators={tElement.currentStatus?.indicators
              ?.filter(i => i.readingType && i.update.live)
              .map(i => new CStatusIndicator(i))}
          />
        );
      },
    },
    currentLocation: generateMultiEntityColumn(
      { field: 'xlocs', headerName: 'Current locations', isFilterable: true },
      { field: 'xlocs', tEntity: 'location', mainClass: CLocation },
      {
        inheritedProps,
        chips: { onlyChips: true },
        onClick: onClickMultiEntityCell,
        generateSearchEntitySelector,
      }
    ),
    defaultLocation: generateEntityColumn(
      { field: 'defaultLocation', headerName: 'Default location' },
      { field: 'defaultLocation', mainClass: CLocation },
      {
        inheritedProps,
        chips: { onlyChips: true },
        onClick: onClickMultiEntityCell,
        generateSearchEntitySelector,
      }
    ),
    attachedDevices: generateMultiEntityColumn(
      { field: 'attachedDevices', headerName: 'Devices attached' },
      { field: 'attachedDevices', mainClass: CDevice },
      { inheritedProps, onClick: onClickMultiEntityCell, generateSearchEntitySelector }
    ),
    createdAt: generateDateColumn({ field: 'createdAt', headerName: 'Created at', isSortable: true }, { field: 'createdAt' }),
    lastActivity: generateDateColumn(
      { field: 'lastActivity', headerName: 'Last activity', isSortable: true },
      { field: 'lastActivity' }
      //{ isFilterable: true }
    ),
    activeNotifications: {
      headerName: 'Active notifications',
      field: 'nActiveNotifications',
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        return tElement.nActiveNotifications ?? 0;
      },
      renderCell: (params: GridRenderCellParams) => {
        const tElement = new CElement(params.row);
        const num = tElement.nActiveNotifications;
        if (num === 0) return '0';
        return (
          <Link
            onClick={() =>
              actStore.setActionTableV3({
                action: 'openModalExtraComp',
                component: Notifications,
                propsComponent: {
                  mode: 'view',
                  fetchData: {
                    filters: {
                      event: { isAlive: true },
                      xEventItems: {
                        isAlive: true,
                        itemId: tElement.getID(),
                        itemType: 'tsElement',
                      },
                    },
                  },
                  title: ['Notifications', ' - ', tElement.getName()],
                  isCurrent: true,
                },
                row: params.row,
              })
            }
          >
            {num}
          </Link>
        );
      },
    },
    openTasks: {
      headerName: 'Tasks not closed',
      field: 'nOpenTasks',
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        return tElement.nOpenTasks ?? 0;
      },
      renderCell: (params: GridRenderCellParams) => {
        const tElement = new CElement(params.row);
        const num = tElement.nOpenTasks;
        if (num === 0) return '0';

        return (
          <Link
            onClick={() =>
              actStore.setActionTableV3({
                action: 'openModalExtraComp',
                component: Tasks,
                title: ['Tasks', ' - ', tElement.getName()],
                propsComponent: {
                  configComponent: { ...TConfigComponent, fetchURL: `/api/tasksByItem/${tElement.getID()}` },
                },
                row: params.row,
              })
            }
          >
            {num}
          </Link>
        );
      },
    },
    labels: generateMultiEntityColumn(
      { field: 'labels', headerName: 'Labels', isFilterable: true },
      { field: 'xItemTags', tEntity: 'tag', mainClass: CLabel },
      {
        inheritedProps,
        chips: { onlyChips: true, limitChips: 20 },
        onClick: onClickMultiEntityCell,
        generateSearchEntitySelector,
      }
    ),
    attachedTo: generateMultiEntityColumn(
      { field: 'attachedTo', headerName: 'Attached to' },
      { field: 'attachedTo', mainClass: CElement },
      { inheritedProps, onClick: onClickMultiEntityCell, generateSearchEntitySelector }
    ),
    elementType: generateEntityColumn(
      {
        field: 'elementType',
        headerName: 'Item type',
        isFilterable: true,
      },
      { tEntity: 'elementType', mainClass: CElementType },
      { inheritedProps, generateSearchEntitySelector }
    ),
    itemsInside: {
      headerName: 'Items inside',
      field: 'itemsInside',
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        return tElement.nItemsInside ?? 0;
      },
      renderCell: (params: GridRenderCellParams) => {
        const tElement = new CElement(params.row);
        const num = tElement.nItemsInside;
        if (num === 0) return '0';
        return (
          <Link
            onClick={() =>
              actStore.setActionTableV3({
                action: 'openModalExtraComp',
                component: GTItemTypesInLocation,
                propsComponent: {
                  title: ['Items inside', ' - ' + tElement.getName() || tElement.getVisualizationID()],
                  mode: 'view',
                  tElement: tElement,
                },
                //row: params.row,
              })
            }
          >
            {num}
          </Link>
        );
      },
    },
    parents: {
      headerName: 'Parents',
      field: 'nHierarchyParents',
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        return tElement.nHierarchyParents ?? 0;
      },
      renderCell: (params: GridRenderCellParams) => {
        const tElement = new CElement(params.row);
        const num = tElement.nHierarchyParents;
        if (num === 0) return '0';
        return (
          <Link
            onClick={() =>
              actStore.setActionTableV3({
                action: 'openModalExtraComp',
                component: HierarchyDataGrid,
                propsComponent: {
                  title: 'Parents',
                  mode: 'view',
                  entity: params.row,
                  isParent: true,
                },
                row: params.row,
              })
            }
          >
            {num}
          </Link>
        );
      },
    },
    children: {
      headerName: 'Children',
      field: 'nHierarchyChilds',
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        return tElement.nHierarchyChilds ?? 0;
      },
      renderCell: (params: GridRenderCellParams) => {
        const tElement = new CElement(params.row);
        const num = tElement.nHierarchyChilds;
        if (num === 0) return '0';
        return (
          <Link
            onClick={() =>
              actStore.setActionTableV3({
                action: 'openModalExtraComp',
                component: HierarchyDataGrid,
                propsComponent: {
                  title: 'Children',
                  mode: 'view',
                  entity: params.row,
                  isChildren: true,
                },
                row: params.row,
              })
            }
          >
            {num}
          </Link>
        );
      },
    },
    startScheduledAndReal: {
      headerName: 'Start, scheduled and real',
      field: 'startScheduledAndReal',
      filterable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        const scheduledTimestamp = tElement.flavors?.process?.scheduledTimestamp;
        const realTimestamp = tElement.flavors?.process?.realTimestamp;

        const date1 = scheduledTimestamp ? new Date(scheduledTimestamp).toISOString() : '';
        const date2 = realTimestamp ? new Date(realTimestamp).toISOString() : '';
        return date1 + ' - ' + date2;
      },
      renderCell: (params: GridRenderCellParams) => {
        const tElement = new CElement(params.row);
        let sched = formatDate(tElement.flavors?.process?.scheduledTimestamp);
        let real = formatDate(tElement.flavors?.process?.realTimestamp);

        return (
          <div className="grid">
            {`${'Sch.'}: ${sched}`}
            {real && <span>{`${'Real'}: ${real}`}</span>}
          </div>
        );
      },
    },
    endScheduledAndReal: {
      headerName: 'End, scheduled and real',
      field: 'endScheduledAndReal',
      filterable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        const scheduledDueTimestamp = tElement.flavors?.process?.scheduledDueTimestamp;
        const realEndTimestamp = tElement.flavors?.process?.realEndTimestamp;

        const date1 = scheduledDueTimestamp ? new Date(scheduledDueTimestamp).toISOString() : '';
        const date2 = realEndTimestamp ? new Date(realEndTimestamp).toISOString() : '';
        return date1 + ' - ' + date2;
      },
      renderCell: (params: GridRenderCellParams) => {
        const tElement = new CElement(params.row);

        let sched = formatDate(tElement.flavors?.process?.scheduledDueTimestamp);
        let real = formatDate(tElement.flavors?.process?.realEndTimestamp);

        return (
          <div style={{ display: 'grid' }}>
            {`${'Sch.'}: ${sched}`}
            {real && <span>{`${'Real'}: ${real}`}</span>}
          </div>
        );
      },
    },
    lastPosition: {
      headerName: 'Last position',
      field: 'lastPosition',
      type: 'string',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      sortable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        let reading = tElement.getLastPosition();
        return JSON.stringify(reading?.data);
      },
      renderCell: (params: GridRenderCellParams) => {
        const tElement = new CElement(params.row);
        let reading = tElement.getLastPosition();
        if (!reading) return;
        return (
          <Tooltip title={formatValue(reading?.data, null, reading.readingTypeId)}>
            <Typography variant="body2" text={formatDate(reading?.timestamp)} />
          </Tooltip>
        );
      },
    },
    //HIDDEN
    lastGNSSCoordinates: {
      headerName: 'Last gnss coordinates',
      field: 'lastGNSSCoordinates',
      type: 'string',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      sortable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        return JSON.stringify(tElement.lastPosition.lastGNSSPosition?.data) ?? null;
      },
    },
    lastGNSSDate: {
      headerName: 'Last gnss date',
      field: 'lastGNSSDate',
      type: 'dateTime',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      sortable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        const reading = tElement.lastPosition.lastGNSSPosition ?? null;
        if (!reading) return;
        const date = new Date(reading?.timestamp);
        return date;
        //return new Date(reading?.timestamp);
      },
      valueFormatter: (value, row, column) => {
        const tElement = new CElement(row);
        const reading = tElement.lastPosition.lastGNSSPosition ?? null;
        if (!reading) return;
        const date = new Date(reading?.timestamp);
        return date.toISOString();
      },
    },
    lastLocalCoordinates: {
      headerName: 'Last local coordinates',
      field: 'lastLocalCoordinates',
      type: 'string',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      sortable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        return JSON.stringify(tElement.lastPosition.lastLocalPosition?.data) ?? null;
      },
    },
    lastLocalDate: {
      headerName: 'Last local date',
      field: 'lastLocalDate',
      type: 'dateTime',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      sortable: false,
      valueGetter: (value, row, column) => {
        const tElement = new CElement(row);
        const reading = tElement.lastPosition.lastLocalPosition ?? null;
        if (!reading) return;
        return new Date(reading?.timestamp);
      },
    },
    itemsContent: {
      headerName: 'Contents',
      field: 'itemsContent',
      type: 'string',
      headerAlign: 'left',
      align: 'left',
      filterable: false,
      sortable: false,
      valueGetter: (value, row, column) => {
        const tProcess = new CProcess(row);
        return tProcess.groupId;
      },
      renderCell: (params: GridRenderCellParams) => {
        const groupId = params.value;

        if (groupId)
          return (
            <Button
              fullWidth
              label={'Show'}
              endIcon={<Icon icon="fact_check" />}
              onClick={() =>
                actStore.setActionTableV3({
                  action: 'openModalItemsContentProcess',
                  propsComponent: {
                    groupId: groupId,
                  },
                  row: params.row,
                })
              }
              variant="outlined"
            />
          );
      },
    },
  };
};

export default ItemColumns;
